// ==================== PROVIDER APPOINTMENTS - NOTIFICATION SYSTEM ====================
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Calendar, Clock, CheckCircle2, XCircle, Loader2, AlertCircle, 
  User, Car, RefreshCw, Bell, MapPin
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

const REFRESH_INTERVAL = 15000; // 15 segundos

export default function ProviderAppointments() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [appointments, setAppointments] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, accepted: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    if (!user || user.user_type !== 'provider') {
      navigate('/provider/login');
      return;
    }
    fetchAppointments();
    
    // Auto-refresh a cada 15 segundos
    const interval = setInterval(() => {
      fetchAppointments(true); // silent refresh
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [user, navigate]);

  const fetchAppointments = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/provider/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(res.data.appointments);
      setCounts(res.data.counts || { pending: 0, accepted: 0, completed: 0 });
      setLastRefresh(new Date());
    } catch (error) {
      if (!silent) {
        toast.error('Erro ao carregar agendamentos');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDecision = async (appointmentId, decision) => {
    if (decision === 'reject' && !confirm('Deseja realmente recusar este agendamento?')) {
      return;
    }

    setActionLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      const endpoint = decision === 'accept' 
        ? `${API}/appointments/${appointmentId}/accept`
        : `${API}/appointments/${appointmentId}/reject`;
      
      await axios.post(
        endpoint,
        decision === 'reject' ? { reason: 'Recusado pelo prestador' } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (decision === 'accept') {
        toast.success('Agendamento confirmado com sucesso!');
      } else {
        toast.success('Agendamento recusado');
      }
      
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar decisão');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (appointmentId) => {
    if (!confirm('Confirmar que o serviço foi concluído?')) return;

    setActionLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/appointments/${appointmentId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Serviço marcado como concluído!');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { text: 'Aguardando sua resposta', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Bell },
      PENDING_PROVIDER_CONFIRMATION: { text: 'Aguardando sua resposta', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Bell },
      ACCEPTED: { text: 'Confirmado', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2 },
      REJECTED: { text: 'Recusado', color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
      CANCELLED: { text: 'Cancelado', color: 'bg-gray-100 text-gray-800 border-gray-300', icon: XCircle },
      CANCELLED_TIMEOUT: { text: 'Cancelado (timeout)', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: AlertCircle },
      COMPLETED: { text: 'Concluído', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: CheckCircle2 },
      EXPIRED: { text: 'Expirado', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: AlertCircle }
    };
    
    const badge = badges[status] || badges.PENDING;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const filterAppointments = (statuses) => {
    return appointments.filter(a => statuses.includes(a.status));
  };

  // Uma reserva vencida (>10 min sem resposta) NUNCA deve aparecer como pendente/acionável,
  // mesmo que o backend ainda não tenha atualizado o status entre um polling e outro (mata o "flash").
  const isEffectivelyExpired = (a) => {
    if (a.status === 'EXPIRED' || a.status === 'CANCELLED_TIMEOUT') return true;
    if ((a.status === 'PENDING' || a.status === 'PENDING_PROVIDER_CONFIRMATION') && a.slot_reserved_at) {
      const deadline = new Date(a.slot_reserved_at).getTime() + 10 * 60 * 1000;
      return Date.now() > deadline;
    }
    return false;
  };
  const pendingAppointments = filterAppointments(['PENDING', 'PENDING_PROVIDER_CONFIRMATION']).filter((a) => !isEffectivelyExpired(a));
  const acceptedAppointments = filterAppointments(['ACCEPTED']);
  const completedAppointments = filterAppointments(['COMPLETED']);
  const expiredAppointments = appointments.filter(isEffectivelyExpired);
  const historyAppointments = filterAppointments(['REJECTED', 'CANCELLED']);

  const renderPendingCard = (appointment) => {
    const isLoading = actionLoading === appointment.id;
    
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-l-4 border-l-yellow-400 bg-yellow-50/30"
        data-testid={`pending-appointment-${appointment.id}`}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-5 h-5 text-slate-600" />
              <p className="font-semibold text-lg">{appointment.client_name}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">
                  {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit'
                  })}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{appointment.time}</span>
              </div>
              
              <div className="flex items-center gap-2 col-span-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span>{appointment.vehicle_brand} {appointment.vehicle_model}</span>
                <Badge variant="outline" className="text-xs">
                  {appointment.vehicle_plate}
                </Badge>
              </div>
              
              <div className="flex items-center gap-2 col-span-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground text-xs">
                  {appointment.service_type === 'complete' ? 'Lavagem Completa' : 'Ducha Simples'}
                </span>
              </div>
            </div>
          </div>
          
          {getStatusBadge(appointment.status)}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => handleDecision(appointment.id, 'accept')}
            disabled={isLoading}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid={`accept-btn-${appointment.id}`}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Aceitar
              </>
            )}
          </Button>
          <Button
            variant="destructive"
            onClick={() => handleDecision(appointment.id, 'reject')}
            disabled={isLoading}
            className="flex-1"
            data-testid={`reject-btn-${appointment.id}`}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Recusar
          </Button>
        </div>
      </Card>
    );
  };

  const renderAcceptedCard = (appointment) => {
    const isLoading = actionLoading === appointment.id;
    
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-l-4 border-l-green-400"
        data-testid={`accepted-appointment-${appointment.id}`}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <p className="font-medium">{appointment.client_name}</p>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {appointment.time}
              </span>
            </div>
            
            <p className="text-sm text-muted-foreground mt-1">
              {appointment.vehicle_brand} {appointment.vehicle_model} - {appointment.vehicle_plate}
            </p>
          </div>
          
          {getStatusBadge(appointment.status)}
        </div>

        <Button
          onClick={() => handleComplete(appointment.id)}
          disabled={isLoading}
          className="w-full"
          data-testid={`complete-btn-${appointment.id}`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Marcar como Concluído
            </>
          )}
        </Button>
      </Card>
    );
  };

  const renderHistoryCard = (appointment) => (
    <Card key={appointment.id} className="p-4 opacity-80">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-medium">{appointment.client_name}</p>
          <p className="text-sm text-muted-foreground">
            {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')} às {appointment.time}
          </p>
          <p className="text-xs text-muted-foreground">
            {appointment.vehicle_brand} {appointment.vehicle_model} - {appointment.vehicle_plate}
          </p>
        </div>
        {getStatusBadge(appointment.status)}
      </div>
    </Card>
  );

  if (loading && appointments.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Agendamentos</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Última atualização: {lastRefresh.toLocaleTimeString('pt-BR')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fetchAppointments()}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button variant="outline" onClick={() => navigate('/provider/dashboard')}>
              Voltar ao Dashboard
            </Button>
          </div>
        </div>

        {/* Alerta de Solicitações Pendentes */}
        {counts.pending > 0 && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-300 border-2">
            <Bell className="h-5 w-5 text-yellow-600" />
            <AlertDescription className="text-yellow-800 ml-2">
              <strong className="text-lg">
                🔔 Você tem {counts.pending} solicitação{counts.pending > 1 ? 'ões' : ''} pendente{counts.pending > 1 ? 's' : ''}!
              </strong>
              <br />
              <span className="text-sm">Responda rapidamente para não perder o cliente.</span>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="pending" className="relative">
              Pendentes
              {counts.pending > 0 && (
                <Badge className="ml-2 bg-yellow-500 text-white">
                  {counts.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="accepted">
              Confirmados
              {counts.accepted > 0 && (
                <Badge variant="outline" className="ml-2">
                  {counts.accepted}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="completed">
              Concluídos
              {counts.completed > 0 && (
                <Badge variant="outline" className="ml-2">
                  {counts.completed}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history">
              Histórico
            </TabsTrigger>
          </TabsList>

          {/* Pendentes */}
          <TabsContent value="pending" className="space-y-4">
            {pendingAppointments.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-muted-foreground">Nenhuma solicitação pendente</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Você receberá um alerta quando houver novas solicitações
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingAppointments.map(renderPendingCard)}
              </div>
            )}
          </TabsContent>

          {/* Confirmados */}
          <TabsContent value="accepted" className="space-y-4">
            {acceptedAppointments.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-muted-foreground">Nenhum agendamento confirmado</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {acceptedAppointments.map(renderAcceptedCard)}
              </div>
            )}
          </TabsContent>

          {/* Concluídos */}
          <TabsContent value="completed" className="space-y-4">
            {completedAppointments.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-muted-foreground">Nenhum serviço concluído ainda</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {completedAppointments.map(renderHistoryCard)}
              </div>
            )}
          </TabsContent>

          {/* Histórico */}
          <TabsContent value="history" className="space-y-3">
            {expiredAppointments.length > 0 && (
              <div className="space-y-3" data-testid="expired-services-section">
                <h3 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Serviços Expirados
                </h3>
                {expiredAppointments.map(renderHistoryCard)}
              </div>
            )}
            {historyAppointments.length === 0 && expiredAppointments.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">Nenhum registro no histórico</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {historyAppointments.map(renderHistoryCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ==================== END PROVIDER APPOINTMENTS ====================
